import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type {
  CatalogItem,
  CatalogItemDetails,
  CollectionResponse,
  Episode,
  ProgressEntry,
  ProviderSource,
  ProviderSourceKind,
  TitleChoice,
  TitleChoiceUpdate,
} from '@lumos/contracts';
import {
  addFavorite,
  createPlaybackSession,
  endPlaybackSession,
  getFavorites,
  getHistory,
  markEntryWatched,
  getItemDetails,
  getCollection,
  getSimilar,
  getItemSources,
  getSeasonEpisodes,
  getSourceVoices,
  getTitleChoice,
  saveTitleChoice,
  removeFavorite,
  resetEntryProgress,
  setReleaseMark,
} from '../api/endpoints.js';
import { ApiError, describeError } from '../api/client.js';
import { BackIcon, FavoritesIcon, FavoritesIconFilled, LumosMark, PlayIcon } from '../components/icons.js';
import { posterSrcSet, tinyImage, washImage } from '../components/images.js';
import { TitleStage, type StageSource } from '../components/TitleStage.js';
import { lastInputWasKeyboard } from '../components/focus-reveal.js';
import { Card } from '../components/Card.js';
import { useInfiniteScroll } from '../components/useInfiniteScroll.js';
import { ChooserSearch } from '../components/ChooserSearch.js';
import { SunLoader } from '../components/SunLoader.js';
import { Loader } from '../components/Loader.js';
import { EpisodeSoon } from '../components/EpisodeSoon.js';
import { episodeLabel, episodeLaunchLabel, episodeNumberLabel, seasonLabel } from './title-labels.js';
import { beginLaunch, type Launch } from '../launch/launch-store.js';
import { soonImage, upcomingEpisodes } from './episode-soon.js';
import { rememberPlayerReturn } from './player-return.js';
import { readLocalProgress } from './player-progress.js';
import { installOverviewFit } from './title-overview-fit.js';
import { titleStepHash } from './title-route.js';

const KIND_LABEL: Record<CatalogItemDetails['kind'], string> = {
  movie: 'Фильм',
  tv: 'Сериал',
};

/**
 * Jellyfin показывается в обоих разделах — «Торренты» и «Смотреть онлайн»:
 * для пользователя это готовый файл-релиз рядом с раздачами, а также источник
 * потока в онлайн-списке. Онлайн-балансеры остаются отдельным разделом.
 */
type UiSourceKind = Extract<ProviderSourceKind, 'online' | 'torrent'>;

type SearchStatus = 'idle' | 'loading' | 'empty' | 'error' | 'ready';

interface SearchState {
  status: SearchStatus;
  sources: ProviderSource[];
  /** Объяснение пустого результата приходит с сервера — не выдумываем своё. */
  notice: string | null;
  /** Поиск ещё идёт — часть источников догрузится (прогрессивный онлайн-поиск). */
  pending: boolean;
}

/** Подпись карточки, когда витрина отдаёт единственную дорожку. */
const SINGLE_TRACK = 'Оригинальная дорожка';

/**
 * Сколько ждать список озвучек перед запуском. Витрина отвечает за секунду-две;
 * не ответившая за десять — повод начать без её ответа, а не держать зрителя
 * перед неподвижным экраном.
 */
const VOICES_WAIT_MS = 10_000;

/** Этапы запуска на слое — теми же словами, какими страница говорила о них и раньше. */
const LAUNCH_PREPARING = 'Подготовка…';
const VOICES_PROGRESS = 'Смотрим, какие есть озвучки…';

const IDLE_SEARCH: SearchState = { status: 'idle', sources: [], notice: null, pending: false };

/**
 * Догрузка онлайн-источников, пока сервер отвечает `pending`: пауза между
 * дозапросами, их предел и общий бюджет времени. Предел обязателен — без него
 * тайтл, у которого ядро витрин так и не сказало «готово», опрашивался бы
 * бесконечно, а зритель смотрел бы на вечное «ещё…».
 */
export const SOURCES_POLL_INTERVAL_MS = 1200;
export const SOURCES_POLL_MAX = 8;
export const SOURCES_POLL_BUDGET_MS = 30_000;

/** Пустой итог онлайн-поиска, когда сервер своего объяснения не прислал. */
const ONLINE_NOT_FOUND = 'Онлайн-источники не найдены.';

const SEARCH_HINT: Record<UiSourceKind, string> = {
  online: 'Нажмите «Смотреть онлайн», чтобы найти онлайн-источники.',
  torrent: 'Нажмите «Торренты», чтобы найти раздачи для этого тайтла.',
};

const SEARCH_PROGRESS: Record<UiSourceKind, string> = {
  online: 'Ищем источники…',
  torrent: 'Ищем раздачи…',
};

/**
 * Пустой выбор: к тайтлу пришли впервые. Отдельная константа, чтобы «ничего не
 * выбрано» выглядело одинаково и до ответа сервера, и после.
 */
const NO_CHOICE: TitleChoice = {
  contentId: '',
  sourceId: null,
  sourceLabel: null,
  voice: null,
  season: null,
  episode: null,
};

/**
 * Чем именно не подошёл источник — словами, а не кодом.
 *
 * «Источник недоступен» и «источник отдаёт не тот контент» для зрителя разные
 * вещи: первое про сбой, второе про подмену, и второе объясняет, почему
 * витрина, которая вчера играла, сегодня пропускается.
 */
function sourceFailureText(error: unknown): string {
  const reason = error instanceof ApiError ? String(error.details?.reason ?? '') : '';
  if (reason === 'content-duration-mismatch') return 'Источник отдаёт не тот контент.';
  if (reason === 'torrent-no-data') return 'У раздачи нет раздающих.';
  return 'Источник недоступен.';
}

/** Запомненный источник среди найденных: сперва по id, затем по подписи. */
/**
 * Тот источник, которым смотрели прошлый раз, — по ответу СЕРВЕРА.
 *
 * Раньше карточка сверяла сама: сперва `sourceId`, потом подпись. У витрин это
 * работало (их id стабилен), у раздач — нет: их id выдаётся заново на каждый
 * поиск, и до подписи доходило почти всегда, а подпись у торрента — имя
 * трекера, то есть находилась «какая-нибудь раздача с того же трекера».
 * Теперь сверку делает сервер по стабильному ключу релиза — тому же, по
 * которому стоят отметки «этой раздачей уже смотрели».
 */
function rememberedSource(sources: ProviderSource[]): ProviderSource | null {
  return sources.find((source) => source.remembered) ?? null;
}

/**
 * Итог поиска, после которого делать нечего, кроме как попробовать снова:
 * «не найдено» или сбой. Кнопка рядом — поиск заново тем же классом, без
 * возврата к описанию: у витрин «не нашлось сейчас» часто значит «ещё не
 * досчитали», и повтор через минуту находит.
 */
function SearchRetry({ text, error = false, onRetry }: {
  text: string;
  error?: boolean;
  onRetry: () => void;
}): React.JSX.Element {
  return (
    <div className={error ? 'state error state-retry is-inline' : 'state state-retry is-inline'}>
      <span>{text}</span>
      <button type="button" className="secondary-action" onClick={onRetry}>
        Повторить
      </button>
    </div>
  );
}

/** Позиция в формате `1:23:45`/`23:45` для подписи «Продолжить с …». */
function formatClock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  return `${h > 0 ? `${h}:` : ''}${mm}:${String(s).padStart(2, '0')}`;
}

/**
 * Страны производства карточки. Ответ сервера не проходит через схему, а поле
 * новое: сервер старой сборки его не пришлёт — страница не должна от этого
 * падать.
 */
function countriesOf(item: CatalogItemDetails): readonly string[] {
  return Array.isArray(item.countries) ? item.countries : [];
}

/**
 * Длинное название ставится кеглем мельче — чтобы в две строки вошло целиком,
 * а не оборвалось многоточием (`data-length` у `.title-name`).
 */
function titleLength(title: string): 'm' | 'l' | undefined {
  if (title.length > 44) return 'l';
  return title.length > 28 ? 'm' : undefined;
}

/** Хронометраж словами: «2 ч 14 мин» читается быстрее, чем «134 мин». */
function formatRuntime(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest} мин`;
  return rest === 0 ? `${hours} ч` : `${hours} ч ${rest} мин`;
}

/**
 * Серия, которую смотреть после досмотренной: следующая в том же сезоне, а за
 * последней — первая серия следующего сезона. Спецвыпуски (сезон 0) в этот
 * порядок не входят: после финала сезона ждут новый сезон, а не «за кадром».
 * `null` — сериал досмотрен до конца.
 */
export function nextEpisode(
  seasons: ReadonlyArray<{ number: number; episodeCount: number }>,
  season: number,
  episode: number,
): { season: number; episode: number } | null {
  const current = seasons.find((entry) => entry.number === season);
  if (current !== undefined && episode < current.episodeCount) return { season, episode: episode + 1 };
  const later = seasons
    .filter((entry) => entry.number > season && entry.episodeCount > 0)
    .sort((a, b) => a.number - b.number)[0];
  return later === undefined ? null : { season: later.number, episode: 1 };
}

/**
 * История тайтла с наложенной позицией ЭТОЙ вкладки.
 *
 * Плеер отправляет позицию и почти сразу закрывается, а карточка спрашивает
 * историю следом — и запросто обгоняет запись. Тогда зритель видит прежнюю
 * полосу: серия, которую он только что смотрел, выглядит нетронутой. Плеер на
 * этот случай оставляет позицию у себя (player-progress.ts), и здесь она
 * накладывается поверх серверного ответа — но только если она СВЕЖЕЕ.
 *
 * Свежесть считается по времени записи, а не по величине позиции: перемотка
 * назад — тоже просмотр, и «больше» не значит «новее».
 */
function withLocalProgress(entries: ProgressEntry[], contentId: string): ProgressEntry[] {
  const local = readLocalProgress(contentId);
  if (local === null) return entries;
  const sameSlot = (entry: ProgressEntry): boolean =>
    entry.contentId === contentId && entry.season === local.season && entry.episode === local.episode;
  const known = entries.find(sameSlot);
  // Сервер уже знает о том же просмотре и знает его не хуже — не спорим.
  if (known !== undefined && Date.parse(known.updatedAt) >= local.at) return entries;
  const merged: ProgressEntry = {
    contentId,
    season: local.season,
    episode: local.episode,
    positionMs: local.positionMs,
    durationMs: local.durationMs,
    percent: local.percent,
    completed: local.completed,
    kept: known?.kept ?? false,
    updatedAt: new Date(local.at).toISOString(),
  };
  // Самая свежая запись идёт первой: по ней выбирается «продолжить».
  return [merged, ...entries.filter((entry) => !sameSlot(entry))];
}

/**
 * Насколько просмотрена серия, в процентах. `null` — её не начинали, и рисовать
 * пустую полосу не надо: пустая полоса читается как «начал и сразу бросил».
 */
function progressOf(entries: ProgressEntry[], episode: { seasonNumber: number; episodeNumber: number }): number | null {
  const entry = entries.find(
    (item) => item.season === episode.seasonNumber && item.episode === episode.episodeNumber,
  );
  if (entry === undefined) return null;
  // Досмотренная серия — всегда полная полоса: под конец процент не дотягивает
  // до ста (титры пропускают), а «почти досмотрено» и «досмотрено» для глаза
  // должны отличаться.
  return entry.completed ? 100 : Math.min(100, Math.max(2, entry.percent));
}

/** Человекочитаемый размер: `1.4 ГБ`, `720 МБ`. `null` — размер неизвестен. */
function formatSize(bytes: number | null | undefined): string | null {
  if (bytes == null || bytes <= 0) return null;
  const units = ['Б', 'КБ', 'МБ', 'ГБ', 'ТБ'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value >= 100 || unit === 0 ? Math.round(value) : value.toFixed(1)} ${units[unit]}`;
}

/**
 * Ряд выбора: подпись слева и варианты справа. Подписи всех рядов стоят в
 * одной левой колонке панели (`.chooser` — сетка, ряд в ней «прозрачен»):
 * «Сезон», «Источник», «Озвучка» читаются одной вертикалью, обычным
 * регистром и тихим цветом (правка владельца 05.10.2026), а не капсом над
 * каждым рядом.
 *
 * Капсулы видны сразу и фокусируются по одной — это удобно с пульта
 * телевизора (стрелки влево-вправо), в отличие от выпадающего списка. Так
 * собран выбор в Lampa. `strip` — лента в одну строку с прокруткой вбок
 * (трекеров бывает много); `plain` — справа не капсулы, а своё содержимое
 * (карточки озвучек).
 */
function ChooserRow({
  label,
  children,
  body = 'capsules',
}: {
  label: string;
  children: React.ReactNode;
  body?: 'capsules' | 'strip' | 'plain';
}): React.JSX.Element {
  const bodyClass = body === 'plain' ? 'chooser-body' : body === 'strip' ? 'capsule-row is-strip' : 'capsule-row';
  return (
    <div className="chooser-row">
      <span className="chooser-label">{label}</span>
      <div className={bodyClass}>{children}</div>
    </div>
  );
}

/**
 * Название озвучки часто несёт бейджи качества в скобках:
 * `Дублированный [HDR10+, 4K]` → имя + метки. Их показываем отдельно, чтобы
 * карточка читалась, а не была одной длинной строкой.
 */
function parseVoice(label: string): { name: string; tags: string[] } {
  const match = /^(.*?)\s*\[(.+)\]\s*$/.exec(label);
  if (match === null) return { name: label, tags: [] };
  return {
    name: match[1]!.trim() || label,
    tags: match[2]!
      .split(/[,;]/)
      .map((t) => t.trim())
      .filter((t) => t.length > 0),
  };
}

/**
 * Озвучки — карточками с кадром из фильма (как prestige-карточки Lampa), а не
 * длинным списком. Один кадр (бэкдроп тайтла) на все варианты: отдельного кадра
 * на озвучку у источника нет, но визуально это гораздо приятнее плоского списка
 * и удобно выбирается с пульта.
 */
function VoiceCards({
  voices,
  selected,
  onSelect,
  image,
  progressPercent,
}: {
  voices: string[];
  selected: string | null;
  onSelect: (voice: string) => void;
  image: string | null;
  /**
   * Насколько просмотрено то, что включится с ЭТОЙ озвучкой. Полосу рисуем
   * только на выбранной карточке: прогресс один на серию, и повторить его на
   * всех вариантах — значит показать одно и то же число столько раз, сколько
   * озвучек, ничего этим не сообщив. А на выбранной он отвечает на настоящий
   * вопрос: «это то, что я смотрел, и вот где я остановился».
   */
  progressPercent: number | null;
}): React.JSX.Element {
  return (
    <div className="variant-grid">
      {voices.map((voice) => {
        const { name, tags } = parseVoice(voice);
        return (
          <button
            key={voice}
            type="button"
            className={voice === selected ? 'variant-card active' : 'variant-card'}
            onClick={() => onSelect(voice)}
          >
            <span className="variant-card-art">
              {image ? <img src={image} alt="" loading="lazy" /> : <span className="variant-card-art-empty" />}
              {voice === selected && <span className="variant-card-check" aria-hidden="true">✓</span>}
              {voice === selected && progressPercent !== null && (
                <span className="card-progress" aria-hidden="true">
                  <span className="card-progress-fill" style={{ width: `${progressPercent}%` }} />
                </span>
              )}
            </span>
            <span className="variant-card-body">
              <span className="variant-card-title">{name}</span>
              {tags.length > 0 && (
                <span className="variant-card-tags">
                  {tags.map((tag) => (
                    <span className="variant-card-tag" key={tag}>
                      {tag}
                    </span>
                  ))}
                </span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Долгое нажатие по строке — на мышке и на тачскрине.
 *
 * Мышке даём ещё и правую кнопку: держать левую полсекунды там неестественно, а
 * «вызвать меню» — привычный жест ровно для такого случая. Клик после
 * сработавшего удержания глотаем: иначе за снятой звёздочкой тут же начался бы
 * просмотр.
 */
function useLongPress(onLongPress: () => void): {
  handlers: {
    onPointerDown: () => void;
    onPointerUp: () => void;
    onPointerLeave: () => void;
    onContextMenu: (event: React.MouseEvent) => void;
  };
  consumed: () => boolean;
} {
  const timer = useRef<number | null>(null);
  const fired = useRef(false);
  const stop = (): void => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
  };
  return {
    handlers: {
      onPointerDown: () => {
        fired.current = false;
        stop();
        timer.current = window.setTimeout(() => {
          fired.current = true;
          onLongPress();
        }, LONG_PRESS_MS);
      },
      onPointerUp: stop,
      onPointerLeave: () => {
        stop();
        fired.current = false;
      },
      onContextMenu: (event) => {
        event.preventDefault();
        onLongPress();
      },
    },
    // Один вопрос — один ответ: флаг гасится сразу, чтобы следующий обычный
    // клик не был проглочен вслед за этим.
    consumed: () => {
      const value = fired.current;
      fired.current = false;
      return value;
    },
  };
}

/** Сколько держать, чтобы это считалось удержанием, а не промахом мимо клика. */
const LONG_PRESS_MS = 500;

function SourceRow({
  source,
  index,
  onPlay,
  busy,
  isSeries = false,
  selected = false,
  played = false,
  onToggleStar,
}: {
  source: ProviderSource;
  index: number;
  onPlay: (source: ProviderSource) => void;
  busy: boolean;
  /** У сериала источник выбирают, а смотреть начинают с конкретной серии. */
  isSeries?: boolean;
  selected?: boolean;
  /** Этой раздачей уже запускались — в списке она помечена звёздочкой. */
  played?: boolean;
  /** Долгое нажатие: поставить или снять отметку руками. */
  onToggleStar?: (source: ProviderSource) => void;
}): React.JSX.Element {
  const longPress = useLongPress(() => onToggleStar?.(source));
  const filesTotal = source.files.reduce((sum, f) => sum + (f.sizeBytes ?? 0), 0);
  const size = formatSize(source.sizeBytes ?? (filesTotal > 0 ? filesTotal : null));
  const seeders = source.availability?.seeders ?? null;
  const playability = source.playability;
  const codec = source.files.find((f) => f.videoCodec)?.videoCodec ?? null;
  const fileCount = source.files.length;
  const hasSubs = source.files.some((f) => f.hasSubtitles);

  return (
    <li
      className={selected ? 'source-item selected' : 'source-item'}
      // Удержание — по ВСЕЙ строке, а не только по кнопке: строка и есть то, на
      // что зритель смотрит и во что тычет. Мышке даём ещё и правую кнопку —
      // держать левую полсекунды там неестественно.
      {...longPress.handlers}
      title="Долгое нажатие (или правый клик) — отметка «уже смотрели»"
    >
      {/*
        Звёздочка «отсюда уже смотрели» — на углу строки, а не в её тексте: в
        списке из восьмидесяти строк отметку ищут взглядом, не читая названий.
      */}
      {played && <span className="source-played" title="Этой раздачей уже запускали">★</span>}
      <div className="source-main">
        {/*
          Главная строка — НАЗВАНИЕ РАЗДАЧИ: по нему и выбирают (там качество,
          озвучки, ремукс это или рип). Подпись провайдера уехала в метки: у
          торрентов это трекер, и он важен, но как признак, а не как заголовок.
          Раньше здесь стояла подпись, и весь список читался как «Jackett»
          пятьдесят раз подряд.
        */}
        <span className="source-title">
          {/* Точка перед названием: в списке из восьмидесяти строк вывод ищут
              взглядом, а не чтением. */}
          {playability !== undefined && playability.verdict !== 'unknown' && (
            <span
              className={`source-dot ${playability.verdict}`}
              title={playability.note ?? 'Скорости хватит с запасом'}
              aria-hidden="true"
            />
          )}
          {source.releaseName || source.title || `Источник ${index + 1}`}
        </span>
        <div className="source-meta">
          {source.providerLabel && <span className="chip chip-accent">{source.providerLabel}</span>}
          {source.quality && <span className="chip">{source.quality}</span>}
          {size && <span className="chip">{size}</span>}
          {seeders != null && <span className="chip">↑ {seeders}</span>}
          {codec && <span className="chip">{codec.toUpperCase()}</span>}
          {fileCount > 1 && <span className="chip">{fileCount} файлов</span>}
          {source.languages.map((lang) => (
            <span className="chip" key={lang}>
              {lang}
            </span>
          ))}
          {hasSubs && <span className="chip">Субтитры</span>}
        </div>
        {/*
          Хватит ли скорости. Считает сервер: у него хронометраж тайтла, а у
          раздачи есть только размер и раздающие — по отдельности они не значат
          ничего. Подпись показываем только когда есть что сказать: зелёному
          объяснение не нужно, он и так виден точкой у названия.
        */}
        {playability?.note != null && (
          <p className={`source-warning ${playability.verdict}`}>{playability.note}</p>
        )}
      </div>
      {/*
        Пустой список файлов — норма для торрент-источника: состав раздачи
        становится известен только после её подъёма в TorrServer. Поэтому
        кнопка активна и без файлов: файл выберет сервер при подготовке.

        У онлайн-источника файлов нет по другой причине: там поток извлекается
        из плеера балансера при создании сессии (ONLINE_SOURCES.md).
      */}
      <button
        type="button"
        className={selected ? 'secondary-action active' : 'secondary-action'}
        disabled={busy}
        onClick={() => {
          if (longPress.consumed()) return;
          onPlay(source);
        }}
      >
        {/* Пока идёт запуск, подпись не меняется: о нём говорит слой запуска
            поверх страницы, а страница под ним стоит неподвижно. */}
        {isSeries ? (selected ? 'Выбран' : 'Выбрать') : 'Смотреть'}
      </button>
    </li>
  );
}

/**
 * Источник по умолчанию.
 *
 * Приоритет сейчас — порядок, в котором источники пришли с сервера: там он и
 * должен решаться, потому что сервер знает состав балансеров и их состояние.
 * Когда появится настройка приоритета (вручную или автоматически), меняться
 * будет сортировка на сервере, а не это место.
 */
function preferredSource(sources: ProviderSource[]): ProviderSource | null {
  return sources[0] ?? null;
}

/**
 * Склеенный список в порядке, заданном администратором (`sourceRank` считает
 * сервер). Сортируем и здесь, а не только на сервере: онлайн-источники приходят
 * несколькими ответами, и витрина, приехавшая второй, иначе встала бы в конец —
 * даже поставленная в настройке первой. Сортировка устойчивая, поэтому внутри
 * одного ранга сохраняется порядок прихода.
 */
function orderedSources(sources: ProviderSource[]): ProviderSource[] {
  return [...sources].sort((a, b) => (a.sourceRank ?? 0) - (b.sourceRank ?? 0));
}

/** Отдельная страница контента (как в Lampa/Jellyseer/Jellyfin), а не модалка. */
export function TitlePage({ contentId, step: routeStep, kind: routeKind }: {
  contentId: string;
  /** Шаг из адреса: `null` — описание. Он и есть текущий шаг страницы. */
  step: 'sources' | 'episodes' | null;
  /** Класс источников из адреса: `null` — зритель его ещё не выбирал. */
  kind: 'online' | 'torrent' | null;
}): React.JSX.Element {
  const [item, setItem] = useState<CatalogItemDetails | null>(null);
  /**
   * Похожее — отдельным запросом, отдельным состоянием и ЛЕНИВО.
   *
   * Ряд под карточкой не должен задерживать её открытие: зритель пришёл
   * смотреть этот фильм, а не подборку вокруг него. Поэтому запрос уходит не
   * при открытии, а когда до ряда действительно долистали, — до этого момента
   * он ничего не стоит. `null` — ещё не спрашивали; пустой массив —
   * «рекомендаций нет», и тогда раздела не будет вовсе.
   */
  const [similar, setSimilar] = useState<CatalogItem[] | null>(null);
  /** Долистали до ряда: с этого мгновения его и грузим. */
  const [similarWanted, setSimilarWanted] = useState(false);
  /**
   * Другие части франшизы — по тем же правилам, что и похожее, но ВЫШЕ него:
   * похожее это догадка витрины о вкусе, а франшиза — факт из карточки, и
   * открывший вторую «Дюну» спрашивает про первую раньше, чем про «что-нибудь
   * в этом духе».
   */
  const [collection, setCollection] = useState<CollectionResponse | null>(null);
  // Тот же наблюдатель, что у бесконечной прокрутки каталога: запас в 600px
  // берётся, чтобы ряд успел приехать до того, как зритель до него доскроллит.
  const similarSentinelRef = useInfiniteScroll(() => setSimilarWanted(true), {
    enabled: !similarWanted,
    rearmKey: contentId,
  });
  const [error, setError] = useState<string | null>(null);
  /** Попытка загрузки карточки: «Повторить» после ошибки просто заводит её заново. */
  const [detailsAttempt, setDetailsAttempt] = useState(0);
  const [search, setSearch] = useState<Record<UiSourceKind, SearchState>>({
    online: IDLE_SEARCH,
    torrent: IDLE_SEARCH,
  });
  /**
   * Какой из двух поисков показывать — ИЗ АДРЕСА, как и шаг.
   *
   * Раньше это было состояние страницы, и оно не переживало перемонтирования.
   * «Назад» из плеера собирает страницу заново, класс сбрасывался в торренты, и
   * эффект автопоиска по адресному шагу немедленно уводил туда зрителя, который
   * смотрел онлайн. Адрес переживает и возврат, и перезагрузку.
   *
   * `torrent` по умолчанию — прежнее поведение для адресов без класса (старые
   * ссылки, закладки).
   */
  const activeKind: UiSourceKind = routeKind ?? 'torrent';
  /**
   * Выбранный трекер в фильтре торрентов; `null` — «Все». Фильтр клиентский:
   * раздачи уже пришли, и повторный поиск ради сужения списка был бы лишним
   * походом к трекерам.
   */
  const [tracker, setTracker] = useState<string | null>(null);
  const [favorite, setFavorite] = useState(false);
  const [favoriteBusy, setFavoriteBusy] = useState(false);
  const [favoriteError, setFavoriteError] = useState<string | null>(null);
  const [playbackBusy, setPlaybackBusy] = useState(false);
  const [playbackMessage, setPlaybackMessage] = useState<string | null>(null);
  const [season, setSeason] = useState<number | null>(null);
  const [episodes, setEpisodes] = useState<Episode[] | null>(null);
  /** Карточка серии, с которой продолжают: к ней подматываем список при открытии. */
  const resumeEpisodeRef = useRef<HTMLButtonElement | null>(null);
  const [episodesNotice, setEpisodesNotice] = useState<string | null>(null);
  /**
   * Незавершённый просмотр фильма («Продолжить с …»). Только для фильмов:
   * прогресс хранится на тайтл, а не на серию, поэтому у сериала он относился бы
   * ко всем эпизодам сразу — как и возобновление в плеере (см. PlayerPage).
   */
  const [resume, setResume] = useState<ProgressEntry | null>(null);
  /**
   * История прочитана (или не пришла): только после этого понятно, какой
   * сезон открывать. Без этой отметки сезон выбирался по тому, что успело
   * прийти первым, — и прогресс, приехавший позже запомненного выбора,
   * уже ничего не менял: список открывался не на той серии «иногда».
   */
  const [progressReady, setProgressReady] = useState(false);
  /** Зритель сам выбрал сезон — автоматика больше его не перебивает. */
  const seasonPickedRef = useRef(false);
  /** Список серий уже докручен к нужной — второй раз не дёргаем. */
  const episodesRevealedRef = useRef(false);
  /**
   * Прогресс по сериям. Отдельно от `resume`, потому что отвечает на другой
   * вопрос: не «с чего продолжить», а «что из этого я уже видел» — и рисуется
   * полосой на каждой карточке серии, как на постере в «Продолжить просмотр».
   */
  const [episodeProgress, setEpisodeProgress] = useState<ProgressEntry[]>([]);
  // Какая серия сейчас показывает меню действий. Одна на весь список: два
  // открытых меню сразу — это вопрос «к какой серии относится», а не удобство.
  const [episodeMenu, setEpisodeMenu] = useState<{ season: number; episode: number } | null>(null);
  /**
   * Счётчик перечитываний прогресса. Растёт, когда к карточке вернулись, — и
   * этим перезапускает запрос истории, не трогая остального состояния экрана
   * (найденных источников, выбранной озвучки, открытого сезона).
   */
  const [progressGeneration, setProgressGeneration] = useState(0);
  /**
   * Выбранный источник. У сериала порядок такой же, как в Lampa: сначала
   * выбирают, откуда смотреть, и только потом серию. Обратный порядок заставлял
   * бы искать источники заново на каждую серию.
   */
  const [selectedSource, setSelectedSource] = useState<ProviderSource | null>(null);
  /**
   * Выбранный источник и то, кем он выбран, — ссылками, а не состоянием.
   *
   * Догрузка витрин идёт в одном длинном цикле, и он видит состояние того
   * рендера, в котором начался: `selectedSource` из него всегда был бы тем, что
   * стояло на первом ответе. Ссылка же показывает сегодняшнее.
   */
  const selectedRef = useRef<string | null>(null);
  const chosenByViewer = useRef(false);
  /**
   * Озвучки выбранного источника и выбор пользователя. У Skaz это отдельные
   * потоки (Дубляж/Оригинал/Сербин…), поэтому выбор — до запуска. Пусто —
   * выбора нет (одна озвучка либо источник его не поддерживает).
   */
  const [voices, setVoices] = useState<string[]>([]);
  const [selectedVoice, setSelectedVoice] = useState<string | null>(null);
  /**
   * Выбранная озвучка ССЫЛКОЙ.
   *
   * Запуск теперь дожидается списка озвучек, а состояние того рендера, в
   * котором нажали, к этому моменту устарело: там ещё пусто. Ссылка показывает
   * то, что выбрано СЕЙЧАС, — иначе ожидание не имело бы смысла.
   */
  const selectedVoiceRef = useRef<string | null>(null);
  /**
   * Озвучки выбранного источника ещё едут.
   *
   * Пока их нет, играть НЕЛЬЗЯ: с пустым выбором дорожку берёт сервер — первую
   * попавшуюся, — и зритель слышит не то, что смотрел в прошлый раз. Раньше на
   * этом месте стояла карточка «Оригинальная дорожка», то есть выбор, которого
   * ещё не существовало, и нажатие по ней ровно это и означало.
   */
  const [voicesPending, setVoicesPending] = useState(false);
  /** Та же загрузка ссылкой: её дожидаются запуск фильма и клик по серии. */
  const voicesLoadRef = useRef<Promise<void> | null>(null);
  /**
   * Поколение загрузки озвучек. Источник подставляется заново на каждый частичный
   * ответ поиска, и поздний ответ прежней витрины не должен перебивать свежий.
   */
  const voicesGen = useRef(0);
  /** Выбор озвучки: состояние для экрана и ссылка для обработчиков — вместе. */
  function applyVoice(voice: string | null): void {
    selectedVoiceRef.current = voice;
    setSelectedVoice(voice);
  }
  /**
   * Забыть озвучки: список к новому источнику (или к новому тайтлу) не
   * относится. Поколение сдвигаем здесь же — иначе ответ прежней витрины,
   * пришедший следом, восстановил бы только что стёртый список.
   */
  function resetVoices(): void {
    voicesGen.current += 1;
    voicesLoadRef.current = null;
    setVoicesPending(false);
    setVoices([]);
    applyVoice(null);
  }
  /**
   * Дождаться озвучек перед запуском.
   *
   * Нажатие не теряется и не игнорируется: зритель уже сказал «смотреть», и
   * правильный ответ — начать ровно тогда, когда станет известно ЧЕМ, а не
   * молча включить первое попавшееся. Пока ждём, слой запуска говорит, чего.
   */
  async function awaitVoices(launch: Launch): Promise<void> {
    const load = voicesLoadRef.current;
    if (load === null) return;
    setPlaybackBusy(true);
    launch.stage(VOICES_PROGRESS);
    // Ожидание ограничено: не ответившая витрина — не повод не начать вовсе.
    // Отмена запуска ожидание тоже снимает: ждать больше нечего.
    await Promise.race([
      load,
      new Promise((resolve) => setTimeout(resolve, VOICES_WAIT_MS)),
      new Promise((resolve) => launch.signal.addEventListener('abort', resolve, { once: true })),
    ]);
    launch.stage(LAUNCH_PREPARING);
  }

  /**
   * Запуск начат: слой поверх страницы — в том же кадре, что и нажатие.
   *
   * Звать ДО первого `await`: отклик на нажатие не должен ждать ни озвучек, ни
   * сервера. Отмена (Esc, «назад») снимает со страницы занятость и недосказанное
   * сообщение о переборе источников — страница возвращается такой, какой была.
   */
  function openLaunch(label: string): Launch {
    return beginLaunch({
      label,
      stage: LAUNCH_PREPARING,
      onCancel: () => {
        setPlaybackBusy(false);
        setPlaybackMessage(null);
      },
    });
  }
  /**
   * Текущий шаг. Экран показывает ровно один: описание с выбором способа
   * просмотра, затем список источников, затем серии. Показывать всё сразу —
   * значит вываливать на пользователя выбор, которого он ещё не делал.
   */
  /**
   * Текущий шаг — ИЗ АДРЕСА, а не из состояния страницы.
   *
   * Так «назад» в браузере возвращает на шаг назад (список раздач → карточка), а
   * не выкидывает со страницы целиком. Отдельная кнопка «свернуть» после этого
   * не нужна: у зрителя уже есть привычная кнопка, и она делает ровно это.
   */
  const step: 'overview' | 'sources' | 'episodes' = routeStep ?? 'overview';
  /**
   * Переход по шагам. Класс источников едет вместе с шагом: он часть ответа на
   * вопрос «где я был», и терять его при переходе список → серии нельзя — иначе
   * возврат на серии снова начинался бы с торрентов.
   *
   * Без явного класса он берётся из АДРЕСА, а не из состояния этого рендера:
   * обработчики живут дольше рендера, и захваченный ими класс успевает
   * устареть (см. `titleStepHash`).
   */
  const setStep = (next: 'overview' | 'sources' | 'episodes', kind?: UiSourceKind): void => {
    const target = titleStepHash(window.location.hash, contentId, next, kind);
    if (window.location.hash !== target) window.location.hash = target;
  };
  /**
   * Запомненный выбор с сервера: источник, озвучка, сезон, серия.
   *
   * В ref, а не в state: его читают эффекты и обработчики по ходу дела, а
   * перерисовывать из-за него страницу незачем — на экране он проявляется через
   * уже существующие состояния (выбранный сезон, источник, озвучка).
   */
  const choiceRef = useRef<TitleChoice>(NO_CHOICE);
  /** Выбор загружен: до этого «ничего не запомнено» и «ещё не знаем» — разные вещи. */
  const [choiceReady, setChoiceReady] = useState(false);

  /** Запомнить выбор: сразу у себя и на сервере — он общий для всех устройств. */
  function rememberChoice(patch: Omit<TitleChoiceUpdate, 'contentId'>): void {
    choiceRef.current = { ...choiceRef.current, ...patch } as TitleChoice;
    void saveTitleChoice({ contentId, ...patch }).catch(() => undefined);
  }

  /**
   * Отметки, снятые или поставленные ЗДЕСЬ И СЕЙЧАС.
   *
   * Сама отметка приходит с сервера прямо в источнике (`played`) — она общая для
   * всех устройств зрителя. Эта карта нужна лишь чтобы звёздочка появилась в тот
   * же миг, когда её поставили, а не после следующего поиска.
   */
  const [playedOverride, setPlayedOverride] = useState<Record<string, boolean>>({});

  /** Отмечена ли раздача: ответ сервера плюс то, что зритель сделал только что. */
  function isPlayed(source: ProviderSource): boolean {
    return playedOverride[source.sourceId] ?? source.played;
  }

  /**
   * Отметка руками. Звёздочку сервер ставит сам при запуске, но зритель знает
   * про раздачу больше: эта не поднялась, а вот эту он смотрел с телефона — и
   * снять или поставить пометку должно быть можно без запуска.
   *
   * Показываем сразу, а на сервер отправляем следом: ждать сеть, чтобы увидеть
   * свою же звёздочку, — заметная задержка на пустом месте. Не получилось —
   * возвращаем как было, иначе список врал бы до следующего поиска.
   */
  function toggleStar(source: ProviderSource): void {
    const next = !isPlayed(source);
    setPlayedOverride((current) => ({ ...current, [source.sourceId]: next }));
    void setReleaseMark(contentId, source.sourceId, next).catch(() => {
      setPlayedOverride((current) => ({ ...current, [source.sourceId]: !next }));
    });
  }
  /** Поколение поиска: отменяет догрузку предыдущего при новом запросе. */
  const searchGen = React.useRef(0);

  // Уход со страницы обрывает догрузку источников. Без этого цикл
  // дозапросов размонтированной страницы жил дальше: вернулся на карточку
  // (из плеера, с главной) — и по одному тайтлу опрашивали сразу два цикла,
  // старый вхолостую, причём и пока зритель был уже совсем на другом экране.
  useEffect(() => () => {
    searchGen.current += 1;
  }, []);

  useEffect(() => {
    setError(null);
    // Поиск прежнего тайтла к этому не относится: его поздний ответ иначе
    // дописал бы чужие источники в новую карточку.
    searchGen.current += 1;
    setSearch({ online: IDLE_SEARCH, torrent: IDLE_SEARCH });
    // Класс источников не сбрасываем: он живёт в адресе, и сбросить его здесь
    // значило бы затереть тот самый класс, с которым зритель на страницу и
    // пришёл.
    setSeason(null);
    setEpisodes(null);
    setProgressReady(false);
    seasonPickedRef.current = false;
    episodesRevealedRef.current = false;
    setSelectedSource(null);
    selectedRef.current = null;
    resetVoices();
    setPlayedOverride({});
    choiceRef.current = NO_CHOICE;
    setChoiceReady(false);
    getTitleChoice(contentId)
      .then((choice) => { choiceRef.current = choice; })
      .catch(() => undefined)
      .finally(() => setChoiceReady(true));
    getItemDetails(contentId)
      .then(setItem)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Не удалось загрузить'));
  }, [contentId, detailsAttempt]);

  // Новый тайтл — новый разговор: и ответ, и сам факт «до ряда долистали»
  // относятся к прежней карточке.
  useEffect(() => {
    setSimilar(null);
    setCollection(null);
    setSimilarWanted(false);
  }, [contentId]);

  // Франшиза грузится тем же маркером, что и похожее: ряды стоят рядом, и
  // отдельный наблюдатель на каждый значил бы два запроса на одно движение.
  useEffect(() => {
    if (!similarWanted || collection !== null) return;
    const abort = new AbortController();
    getCollection(contentId, abort.signal)
      .then(setCollection)
      .catch(() => setCollection({ contentId, name: null, items: [], notice: null }));
    return () => abort.abort();
  }, [contentId, collection, similarWanted]);

  // Похожее грузим, когда до него долистали, и молча: ряд — украшение карточки,
  // и его отсутствие ничего не значит для того, зачем зритель сюда пришёл.
  useEffect(() => {
    if (!similarWanted || similar !== null) return;
    const abort = new AbortController();
    getSimilar(contentId, abort.signal)
      .then((response) => setSimilar(response.items))
      .catch(() => setSimilar([]));
    return () => abort.abort();
  }, [contentId, similar, similarWanted]);

  // Прогресс тайтла: у фильма одна запись, у сериала — по записи на каждую
  // начатую серию. Тянем всё разом: серий десятки, а запрос один, и по этим же
  // данным рисуются полосы на карточках серий.
  /**
   * Отметить серию просмотренной или сбросить её прогресс.
   *
   * После действия перечитываем прогресс серий целиком: полоса на карточке
   * рисуется по нему, и обновлять её вручную значило бы держать две версии
   * правды — свою и серверную.
   */
  const applyEpisodeAction = useCallback(
    async (action: 'watched' | 'reset', episode: { seasonNumber: number; episodeNumber: number }) => {
      const spot = { season: episode.seasonNumber, episode: episode.episodeNumber };
      setEpisodeMenu(null);
      try {
        if (action === 'watched') await markEntryWatched(contentId, spot);
        else await resetEntryProgress(contentId, spot);
      } catch {
        // Молчим намеренно: не вышло — полоса просто останется прежней, и это
        // честнее, чем показать изменение, которого не произошло.
        return;
      }
      const history = await getHistory().catch(() => null);
      if (history === null) return;
      const mine = withLocalProgress(
        history.entries.filter((entry) => entry.contentId === contentId),
        contentId,
      );
      setEpisodeProgress(mine.filter((entry) => entry.episode !== null));
    },
    [contentId],
  );

  useEffect(() => {
    setResume(null);
    setEpisodeProgress([]);
    if (!item) return;
    let cancelled = false;
    getHistory()
      .then((history) => {
        if (cancelled) return;
        const mine = withLocalProgress(
          history.entries.filter((e) => e.contentId === contentId),
          contentId,
        );
        setEpisodeProgress(mine.filter((e) => e.episode !== null));
        // «Продолжить» показываем по самой свежей записи — у сериала это
        // последняя смотренная серия. Границы те же, что у возобновления в
        // плеере: не в самом начале и не у конца (это уже «досмотрено»).
        const entry = mine[0];
        if (!entry) return;
        const finished = entry.completed
          || (entry.durationMs !== null && entry.positionMs > entry.durationMs - 60_000);
        if (finished) {
          // Досмотренная серия — не конец сериала: продолжают СЛЕДУЮЩУЮ, и
          // кнопка, рамка в списке серий и открытый сезон ведут к ней. Позиции
          // у неё ещё нет — плеер начнёт с начала.
          const next = entry.season !== null && entry.episode !== null
            ? nextEpisode(item.seasons, entry.season, entry.episode)
            : null;
          if (next !== null) {
            setResume({
              ...entry,
              season: next.season,
              episode: next.episode,
              positionMs: 0,
              durationMs: null,
              percent: 0,
              completed: false,
            });
          }
          return;
        }
        if (entry.positionMs < 15_000) return;
        setResume(entry);
      })
      .catch(() => undefined)
      // Готовность — ПОСЛЕ разбора истории: сезон выбирается по `resume`, и
      // увидеть готовность раньше него значило бы открыть первый сезон.
      .finally(() => {
        if (!cancelled) setProgressReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [item, contentId, progressGeneration]);

  /**
   * Перечитывание прогресса при возвращении к карточке.
   *
   * Экран плеера и карточка — одна и та же вкладка, и вернуться на карточку
   * можно, не перезагружая её: «назад» в браузере, переключение вкладок,
   * телевизор, вышедший из сна. История в этот момент уже другая, а карточка
   * показывала ту, что прочитала при открытии.
   *
   * `pageshow` здесь не лишний: возврат из bfcache не поднимает ни
   * `visibilitychange`, ни `focus`, а страница при этом та же самая.
   */
  useEffect(() => {
    const refresh = (): void => {
      if (document.visibilityState === 'hidden') return;
      setProgressGeneration((value) => value + 1);
    };
    window.addEventListener('focus', refresh);
    window.addEventListener('pageshow', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      window.removeEventListener('pageshow', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, []);

  // Первый сезон открывается сам: пустой экран со списком сезонов ничего не
  // объясняет, а состав серий — это то, ради чего страницу сериала и открыли.
  useEffect(() => {
    if (!item || item.kind !== 'tv' || season !== null || !choiceReady || !progressReady) return;
    if (seasonPickedRef.current) return;
    // Порядок важен: сперва то, что реально смотрели последним (по прогрессу),
    // затем запомненный выбор, и только потом первый сезон. Первый сезон —
    // ответ для того, кто пришёл впервые, а не для того, кто вернулся за
    // следующей серией.
    const watched = resume?.season ?? null;
    const remembered = choiceRef.current.season;
    const known = new Set(item.seasons.map((entry) => entry.number));
    if (watched !== null && known.has(watched)) {
      setSeason(watched);
      return;
    }
    if (remembered !== null && known.has(remembered)) {
      setSeason(remembered);
      return;
    }
    const first = item.seasons.find((entry) => entry.number > 0) ?? item.seasons[0];
    if (first) setSeason(first.number);
    // `choiceReady` в зависимостях намеренно: до ответа сервера мы не знаем,
    // какой сезон смотрели прошлый раз, и подставленный первый пришлось бы
    // менять у зрителя на глазах — вместе с уже запущенным поиском раздач.
  }, [item, season, resume, contentId, choiceReady, progressReady]);

  useEffect(() => {
    if (!item || item.kind !== 'tv' || season === null) return;
    let cancelled = false;
    setEpisodes(null);
    setEpisodesNotice(null);
    getSeasonEpisodes(contentId, season)
      .then((response) => {
        if (cancelled) return;
        setEpisodes(response.episodes);
        setEpisodesNotice(response.notice);
      })
      .catch(() => {
        if (cancelled) return;
        setEpisodes([]);
        setEpisodesNotice('Не удалось загрузить список серий.');
      });
    return () => {
      cancelled = true;
    };
  }, [contentId, item, season]);

  async function loadSources(
    kind: UiSourceKind,
    seasonOverride?: number,
    options?: { keepStep?: boolean },
  ): Promise<void> {
    // Новый поиск — новый набор трекеров, прежний выбор к нему не относится.
    setTracker(null);
    chosenByViewer.current = false;
    // Метка поколения: новый поиск (смена класса/сезона, повторный клик) отменяет
    // догрузку предыдущего — иначе поздний ответ старого поллинга перезаписал бы
    // текущий список.
    const gen = (searchGen.current += 1);
    // Выбор способа сделан — уходим на экран источников, а описание и кнопки
    // убираем: они своё отработали. Класс уезжает в адрес тем же переходом:
    // отдельного состояния у него больше нет.
    //
    // `keepStep` — для восстановления: когда поиск начинает не зритель, а
    // возврат на уже открытый адрес, шаг менять НЕЛЬЗЯ. Иначе вернувшийся к
    // списку серий оказывался бы на списке источников — то есть снова не там,
    // откуда уходил.
    if (options?.keepStep !== true) setStep('sources', kind);
    setSelectedSource(null);
    selectedRef.current = null;
    resetVoices();
    setSearch((previous) => ({ ...previous, [kind]: { ...IDLE_SEARCH, status: 'loading' } }));
    const scope = searchScope(seasonOverride);
    // Накопитель по sourceId: онлайн-источники приходят частями и склеиваются по
    // стабильному id (сервер регистрирует их идемпотентно).
    const byId = new Map<string, ProviderSource>();
    const apply = (notice: string | null, pending: boolean): void => {
      const sources = orderedSources([...byId.values()]);
      setSearch((previous) => ({
        ...previous,
        [kind]: {
          status: sources.length > 0 ? 'ready' : pending ? 'loading' : 'empty',
          sources,
          notice,
          pending,
        },
      }));
    };
    try {
      let response = await getItemSources(contentId, kind, scope);
      if (searchGen.current !== gen) return;
      for (const source of response.sources) byId.set(source.sourceId, source);
      apply(response.notice, response.pending ?? false);

      // Приоритетный источник подставляем сразу для ОНЛАЙНА: там выбор — это
      // компактная панель «Источник · Озвучка · Смотреть», и готовая подстановка
      // экономит клик.
      //
      // У торрентов авто-выбора нет, и у сериала тоже. Раньше он был, и список
      // раздач у сериала просто не показывался: подставленный источник сразу
      // уводил на серии, минуя выбор. Между раздачами выбирают по трекеру,
      // качеству, размеру и сидам — за пользователя этого не решить.
      // Подставляем ПЕРВЫЙ ПО ПОРЯДКУ, а не первый пришедший: список приезжает
      // частями, и без сортировки сюда попадала витрина, которая просто успела
      // раньше, — та, что стоит в настройке первой, оставалась ниже.
      const list = orderedSources([...byId.values()]);
      if (kind === 'online') {
        const preferred = rememberedSource(list) ?? preferredSource(list);
        if (preferred !== null) void selectSource(preferred, { auto: true });
      }

      // Прогрессивная догрузка: пока сервер сообщает `pending`, дозапрашиваем тот
      // же endpoint и дозаполняем список — источники появляются по мере готовности,
      // как в Lampa, а не одним разом после ожидания.
      //
      // Предел — и по числу дозапросов, и по времени: холодный тайтл сказ ищет
      // 10–20 с, и дольше ждать за зрителя незачем. Исчерпали — список
      // считается окончательным, а пустой — честным «не найдено» с «Повторить».
      let pending = response.pending ?? false;
      const deadline = Date.now() + SOURCES_POLL_BUDGET_MS;
      let tries = 0;
      for (; kind === 'online' && pending && tries < SOURCES_POLL_MAX && Date.now() < deadline; tries += 1) {
        await new Promise((resolve) => setTimeout(resolve, SOURCES_POLL_INTERVAL_MS));
        if (searchGen.current !== gen) return;
        response = await getItemSources(contentId, kind, scope);
        if (searchGen.current !== gen) return;
        for (const source of response.sources) byId.set(source.sourceId, source);
        pending = response.pending ?? false;
        apply(response.notice, pending);
        // Пока выбор наш, а не зрителя, пересматриваем его на каждом ответе:
        // витрина, которую администратор поставил первой, часто приезжает
        // второй, и подставленная из первого ответа так и осталась бы стоять.
        if (kind === 'online' && !chosenByViewer.current) {
          const list = orderedSources([...byId.values()]);
          const preferred = rememberedSource(list) ?? preferredSource(list);
          if (preferred !== null && preferred.sourceId !== selectedRef.current) {
            void selectSource(preferred, { auto: true });
          }
        }
      }
      // Исчерпали бюджет догрузки, а сервер всё ещё pending: прекращаем поллинг и
      // снимаем индикатор — список уже большой и считается финальным, иначе
      // спиннер «ещё…» висел бы вечно.
      if (pending && searchGen.current === gen) apply(response.notice, false);
    } catch {
      if (searchGen.current !== gen) return;
      setSearch((previous) => ({ ...previous, [kind]: { ...IDLE_SEARCH, status: 'error' } }));
    }
  }

  /**
   * Шаг выбора открыт адресом (перезагрузка страницы, ссылка, «вперёд» в
   * браузере), а поиска ещё не было — значит его надо начать.
   *
   * Раньше шаг жил только в памяти страницы, и такого случая не существовало.
   * Теперь он в адресе, и без этого зритель попадал бы на список источников с
   * подсказкой «нажмите Торренты» — на экране, куда он уже пришёл именно за
   * ними.
   */
  useEffect(() => {
    if (item === null || routeStep === null) return;
    if (search[activeKind].status !== 'idle') return;
    if (item.kind === 'tv' && season === null) return;
    // Шаг уже задан адресом — восстанавливаем поиск ПОД него, а не переводим
    // зрителя на другой экран.
    void loadSources(activeKind, undefined, { keepStep: true });
    // Зависимости — только то, от чего зависит РЕШЕНИЕ начать поиск: сама
    // функция поиска пересоздаётся на каждый рендер, и её присутствие в списке
    // закрутило бы цикл.
  }, [item, routeStep, activeKind, season, search]);

  useEffect(() => {
    getFavorites()
      .then((response) => setFavorite(response.items.some((entry) => entry.id === contentId)))
      .catch(() => undefined);
  }, [contentId]);

  /**
   * Сезон для поиска источников. Номер серии здесь не нужен: раздача сезона
   * одна на все его серии, а у онлайн-копии серии лежат внутри неё.
   */
  function searchScope(seasonOverride?: number): { season: number } | undefined {
    if (item?.kind !== 'tv') return undefined;
    const value = seasonOverride ?? season;
    return value === null ? undefined : { season: value };
  }

  /**
   * Смена сезона на шаге источников. Найденное относилось к прошлому сезону —
   * раздача сезона одна на его серии, и предлагать её для другого нельзя.
   * Поэтому поиск повторяется, а выбранный источник сбрасывается.
   */
  function chooseSeason(next: number): void {
    seasonPickedRef.current = true;
    episodesRevealedRef.current = true;
    setSeason(next);
    // Запоминаем сам ВЫБОР сезона, а не только уход в плеер: зритель открывает
    // сериал, переключает сезон и уходит — вернувшись, он вправе увидеть тот
    // сезон, который выбрал, а не тот, что открылся сам.
    //
    // Серию при этом сбрасываем: запомненная относилась к ПРЕЖНЕМУ сезону, и,
    // пережив переключение, она открывала бы десятую серию там, где их пять.
    rememberChoice({ season: next, episode: null });
    setSelectedSource(null);
    selectedRef.current = null;
    resetVoices();
    if (search[activeKind].status !== 'idle') {
      void loadSources(activeKind, next);
    }
  }

  /**
   * Выбор источника в панели: запоминает его и лениво тянет озвучки (они есть
   * только у онлайн-источников). Просмотр НЕ запускает — у фильма это делает
   * кнопка «Смотреть», у сериала клик по серии: так выбор источника, озвучки и
   * серии собран в одном месте, как в Lampa, а не разбросан по шагам.
   */
  async function selectSource(source: ProviderSource, opts?: { auto?: boolean }): Promise<void> {
    // Нажал сам — догрузка витрин его выбор больше не трогает.
    if (opts?.auto !== true) chosenByViewer.current = true;
    selectedRef.current = source.sourceId;
    setSelectedSource(source);
    setPlaybackMessage(null);
    // Прежние озвучки относились к прежней витрине: держать их на экране, пока
    // едут новые, значит предлагать выбор, которого у этого источника нет.
    resetVoices();
    // Осознанный выбор пользователя запоминаем; авто-подстановку — нет, иначе
    // первый же авто-выбор затёр бы прошлый выбор.
    if (opts?.auto !== true) {
      rememberChoice({ sourceId: source.sourceId, sourceLabel: source.providerLabel ?? source.title });
    }
    if (source.kind !== 'online') {
      if (item?.kind === 'tv') setStep('episodes');
      return;
    }
    const gen = voicesGen.current;
    setVoicesPending(true);
    const load = (async () => {
      let list: string[] = [];
      let remembered: string | null = null;
      try {
        // У сериала озвучки живут на уровне СЕРИИ, а не копии целиком:
        // спрашиваем за открытый сезон и его первую серию. Студии по сериям
        // одного сезона совпадают, поэтому первой достаточно, а без неё список
        // приходил пустым — выбора озвучки у сериалов не было вовсе.
        const forEpisode = item?.kind === 'tv' && season !== null
          ? { season, episode: episodes?.[0]?.episodeNumber ?? 1 }
          : undefined;
        const response = await getSourceVoices(contentId, source.sourceId, forEpisode);
        list = response.voices;
        remembered = response.remembered;
      } catch {
        list = [];
      }
      // Источник успели сменить — этот ответ уже не про то, что на экране.
      if (voicesGen.current !== gen) return;
      setVoices(list);
      // Запомненную озвучку называет СЕРВЕР: помнит он же и помнит по витрине.
      // Одну и ту же студию витрины называют по-разному («LostFilm» в Alloha,
      // «LostFilm (aac)» в KinoPub), и одна строка на тайтл теряла выбор при
      // каждой смене витрины.
      applyVoice(remembered ?? list[0] ?? null);
      setVoicesPending(false);
    })();
    voicesLoadRef.current = load;
    await load;
    if (item?.kind === 'tv') setStep('episodes');
  }

  /**
   * Клик по раздаче.
   *
   * У фильма это «смотреть» — выбирать больше нечего. У сериала выбор раздачи
   * лишь первый шаг: дальше нужна серия, и список раздач уступает место списку
   * серий (так же устроен выбор в Lampa). Играть сразу нельзя — включилась бы
   * первая попавшаяся серия вместо той, которую хотели.
   */
  async function chooseTorrent(source: ProviderSource): Promise<void> {
    if (item?.kind !== 'tv') {
      await startPlayback(openLaunch(item?.title ?? ''), source);
      return;
    }
    setSelectedSource(source);
    setPlaybackMessage(null);
    rememberChoice({ sourceId: source.sourceId, sourceLabel: source.providerLabel ?? source.title });
    setStep('episodes');
  }

  /** Выбор озвучки пользователем: запоминаем на тайтл. */
  /**
   * Выбрать дорожку и сразу смотреть — это последний шаг у фильма.
   * `SINGLE_TRACK` — псевдодорожка для витрины, у которой выбора нет.
   */
  async function playWithVoice(voice: string): Promise<void> {
    if (selectedSource === null) return;
    const launch = openLaunch(item?.title ?? '');
    const real = voice === SINGLE_TRACK ? undefined : voice;
    if (real !== undefined) chooseVoice(real);
    // Нажали на псевдодорожку — значит выбора зритель не делал, и запускать
    // нужно тем, что назовёт список. Дожидаемся его: до ответа витрины «одна
    // дорожка» и «мы ещё не знаем, сколько их» неотличимы.
    else await awaitVoices(launch);
    if (launch.cancelled) return;
    await startPlayback(launch, selectedSource, undefined, real ?? selectedVoiceRef.current ?? undefined);
  }

  function chooseVoice(voice: string): void {
    applyVoice(voice);
    // Источник шлём вместе с озвучкой: помнит её сервер по витрине, а какая
    // витрина открыта — знает только карточка.
    rememberChoice({ voice, sourceId: selectedSource?.sourceId ?? undefined });
  }

  /**
   * Клик по серии. Без выбранного источника играть нечем, но промолчать нельзя:
   * пользователь уже выразил намерение, и правильный ответ — сказать, чего не
   * хватает, и показать где, а не проигнорировать нажатие.
   */
  /**
   * Подпись серии для кнопки «Продолжить»: у фильма — с какой минуты, у
   * сериала — «Сезон 1, Эпизод 4» словами целиком. Без номера кнопка обещала
   * бы «продолжить», не говоря что именно. На узком экране сезон уходит
   * (`.resume-season`): «Продолжить · Эпизод 4» помещается в одну строку.
   */
  const resumeLabel: React.ReactNode = resume === null
    ? ''
    : resume.season != null && resume.episode != null
      ? (
        <>
          {' · '}
          <span className="resume-season">{`Сезон ${resume.season}, `}</span>
          {episodeNumberLabel(resume.episode)}
        </>
      )
      : ` · ${formatClock(resume.positionMs)}`;

  /**
   * Продолжить с того места, где остановились.
   *
   * Единственная разница с обычным запуском — у сериала: открываем ТОТ сезон и
   * ту серию, на которой человек остановился, а не первый сезон заново. Сама
   * позиция внутри серии восстанавливается плеером по истории, поэтому передавать
   * её сюда не нужно.
   */
  async function continueWatching(): Promise<void> {
    if (resume?.season != null && resume.episode != null) {
      setSeason(resume.season);
      rememberChoice({ season: resume.season, episode: resume.episode });
    }
    await loadSources('online');
  }

  async function playEpisode(episode: Episode): Promise<void> {
    // На этот шаг нельзя попасть без выбранного источника, но проверка нужна:
    // состояние может быть сброшено переходом назад.
    if (selectedSource === null) {
      setStep('sources');
      return;
    }
    // Запоминаем ДО ухода в плеер: вернувшись на карточку, пользователь должен
    // увидеть тот же сезон и ту же серию, а не первый сезон заново.
    rememberChoice({ season: episode.seasonNumber, episode: episode.episodeNumber });
    const launch = openLaunch(episodeLaunchLabel(episode.seasonNumber, episode.episodeNumber, episode.name));
    // Серию нажимают, не дожидаясь озвучек: на экране они рядом, и список серий
    // появляется раньше их ответа. Ждём — иначе дорожку выбрал бы сервер.
    await awaitVoices(launch);
    if (launch.cancelled) return;
    await startPlayback(launch, selectedSource, episode, selectedVoiceRef.current ?? undefined);
  }

  /**
   * Создаёт сессию и уходит в плеер. Для сериала передаётся выбранная серия:
   * по ней сервер находит файл в раздаче или ссылку внутри онлайн-копии.
   *
   * Автофолбэк: если источник не воспроизвести (`PLAYBACK_SOURCE_UNAVAILABLE` —
   * у онлайн-балансера истёк вариант, нет запрошенной серии или он временно
   * недоступен), молча пробуем следующий источник того же класса. У онлайн-копий
   * это норма — рабочих балансеров несколько, и заставлять пользователя вручную
   * перебирать их значило бы показывать ошибку там, где система может справиться
   * сама. Ошибку показываем, только когда не сыграл ни один.
   *
   * `launch` — слой запуска, открытый нажатием: сюда идут этапы перебора, им же
   * запуск уходит в плеер или отменяется. Отменённый запуск ничего не трогает:
   * ни адреса, ни сообщений страницы, а успевшую создаться сессию закрывает.
   */
  async function startPlayback(
    launch: Launch,
    source: ProviderSource,
    episode?: Episode,
    voice?: string,
  ): Promise<void> {
    // Автоперебор — только для ОНЛАЙНА, и на то две причины.
    //
    // Первая: у онлайна следующий источник это та же копия с другой витрины, а
    // следующая раздача — другой релиз. Он может оказаться другого качества, с
    // другой озвучкой и другой длительности; молча подменять выбор
    // пользователя на такое нельзя.
    //
    // Вторая тяжелее. Каждая попытка с торрентом оставляет след В ДВИЖКЕ: он
    // добавляет раздачу и начинает искать пиров. Перебор десятка раздач подряд
    // накапливает у TorrServer столько же мёртвых торрентов, и он перестаёт
    // отвечать целиком — наблюдалось не раз, вплоть до отказа `list`. То есть
    // перебор не просто бесполезен, а вредит движку.
    const candidates = activeKind === 'online'
      ? [source, ...search[activeKind].sources.filter((entry) => entry.sourceId !== source.sourceId)]
      : [source];
    setPlaybackBusy(true);
    setPlaybackMessage(null);
    // Чем не подошёл предыдущий кандидат — подставляется в сообщение о переборе.
    let failure = 'Источник недоступен.';
    // Запуск ушёл в плеер: страница доживает последний кадр под слоем и должна
    // остаться в нём такой, какой была, — без снятой занятости.
    let handedOff = false;
    try {
      for (let index = 0; index < candidates.length; index += 1) {
        const candidate = candidates[index]!;
        const last = index === candidates.length - 1;
        if (index > 0) {
          const trying = `${failure} Пробуем следующий (${index + 1}/${candidates.length})…`;
          setPlaybackMessage(trying);
          launch.stage(trying);
        }
        // `fileId` передаём, только если состав раздачи уже известен: иначе файл
        // выбирает сервер после подъёма источника в TorrServer.
        const file = candidate.files[0];
        try {
          const session = await createPlaybackSession(contentId, {
            sourceId: candidate.sourceId,
            ...(file ? { fileId: file.fileId } : {}),
            ...(episode
              ? { episode: { season: episode.seasonNumber, episode: episode.episodeNumber } }
              : {}),
            // Озвучка относится к выбранному источнику; при фолбэке на другой
            // источник её не переносим — там свои озвучки/дефолт.
            ...(index === 0 && voice ? { voice } : {}),
            // Зритель отменил запуск — запрос снимается, сервер поток не готовит.
            signal: launch.signal,
          });
          const watchHash = `#/watch/${encodeURIComponent(session.sessionId)}/${encodeURIComponent(contentId)}`;
          const abandon = (): void => {
            void endPlaybackSession(session.sessionId).catch(() => undefined);
          };
          // Отменили, пока сервер отвечал: сессия есть, а смотреть её некому.
          if (launch.cancelled) {
            abandon();
            return;
          }
          // Запоминаем ТОТ источник, который заиграл, а не тот, который выбрали
          // и который подвёл: иначе при следующем открытии перебор начнётся с
          // той же неудачи, и «источник недоступен» будет встречать зрителя
          // снова и снова.
          // Сам выбор сервер запомнил при создании сессии; здесь держим свою
          // копию в согласии с ним, чтобы следующий шаг на этом же экране видел
          // то же самое.
          choiceRef.current = { ...choiceRef.current, sourceId: candidate.sourceId, sourceLabel: candidate.title };
          // Отметку «уже смотрели» поставил сервер при создании сессии; здесь
          // лишь показываем её сразу, не перечитывая список.
          setPlayedOverride((current) => ({ ...current, [candidate.sourceId]: true }));
          // Откуда ушли — туда и вернёмся: плеер знает сессию и тайтл, но не
          // знает экрана, на котором стоял зритель. Запоминаем ДО перехода,
          // пока текущий адрес ещё наш.
          rememberPlayerReturn(window.location.hash);
          // Играть можно только тем путём, который выдал сервер; собственного URL
          // потока у клиента нет и быть не может. Адрес меняет слой запуска: он
          // же остаётся на экране экраном загрузки плеера до первого кадра.
          launch.handoff(session.sessionId, watchHash, abandon);
          handedOff = true;
          return;
        } catch (e) {
          // Отмена снимает запрос, и он падает — это не отказ источника.
          if (launch.cancelled) return;
          // Перебираем дальше только на «этот источник не сыграть». Прочие
          // ошибки (нет провайдеров, отказ прав) один источник не лечит, и
          // молча долбить остальные бессмысленно — показываем сразу.
          const retryable = e instanceof ApiError && e.code === 'PLAYBACK_SOURCE_UNAVAILABLE';
          if (retryable && !last) {
            // Причину называем ту, что вернул сервер: «недоступен» и «отдаёт не
            // тот контент» — разные вещи, и вторая объясняет, почему источник,
            // который вчера играл, сегодня пропускается.
            failure = sourceFailureText(e);
            continue;
          }
          const reason = e instanceof ApiError || e instanceof Error
            ? describeError(e)
            : 'Воспроизведение пока недоступно.';
          setPlaybackMessage(reason);
          // Причину говорит и слой — строка страницы стоит под списком и часто
          // за краем экрана; затем он уходит, вернув страницу и фокус.
          launch.fail(reason);
          return;
        }
      }
    } finally {
      // Серии и раздачи под слоем на время запуска приглушены. Снять это в миг
      // ухода — значит на один кадр высветлить страницу, которая как раз
      // растворяется в плеер; отмена и отказ занятость снимают сами.
      if (!handedOff) setPlaybackBusy(false);
    }
  }

  async function toggleFavorite(): Promise<void> {
    setFavoriteBusy(true);
    setFavoriteError(null);
    try {
      if (favorite) await removeFavorite(contentId);
      else await addFavorite(contentId);
      setFavorite((value) => !value);
    } catch (e) {
      if (e instanceof ApiError || e instanceof Error) setFavoriteError(describeError(e));
      else setFavoriteError('Не удалось изменить избранное');
    } finally {
      setFavoriteBusy(false);
    }
  }

  const back = (): void => {
    window.location.hash = '#/';
  };

  const active = search[activeKind];
  /**
   * Трекеры найденного, по убыванию числа раздач. Считаем по всему списку, а не
   * по отфильтрованному: счётчики в кнопках не должны меняться от того, какая
   * кнопка нажата.
   */
  const trackers = [...active.sources.reduce((counts, source) => {
    const name = source.providerLabel ?? 'Прочее';
    const seen = counts.get(name);
    return counts.set(name, {
      count: (seen?.count ?? 0) + 1,
      rank: Math.min(seen?.rank ?? Number.MAX_SAFE_INTEGER, source.sourceRank ?? 0),
    });
  }, new Map<string, { count: number; rank: number }>())]
    // Сначала порядок, заданный администратором, и только внутри одного ранга —
    // по числу раздач: у неупорядоченных трекеров ранг общий, и для них правило
    // остаётся прежним.
    .sort((a, b) => a[1].rank - b[1].rank || b[1].count - a[1].count)
    .map(([name, entry]) => [name, entry.count] as const);
  const visibleSources = tracker === null
    ? active.sources
    : active.sources.filter((source) => (source.providerLabel ?? 'Прочее') === tracker);
  /**
   * Последняя игранная серия — её карточку подсвечиваем. Берём из прогресса,
   * а запомненный выбор используем запасным путём: прогресс честнее (он пишется
   * во время просмотра), но у только что начатой серии его может ещё не быть.
   */
  /**
   * Насколько просмотрено то, что продолжится: у фильма — он сам, у сериала —
   * последняя начатая серия. `null` — продолжать нечего.
   */
  const watchedPercent = resume === null
    ? null
    : resume.completed ? 100 : Math.min(100, Math.max(2, resume.percent));
  /** Полоса под «Продолжить» — только когда позиция уже есть (у следующей серии её нет). */
  const showProgress = resume !== null && resume.positionMs > 0 && watchedPercent !== null;
  const lastEpisode = resume?.episode !== null && resume?.season != null
    ? { season: resume.season, episode: resume.episode }
    : (() => {
        const choice = choiceRef.current;
        return choice.season !== null && choice.episode !== null
          ? { season: choice.season, episode: choice.episode }
          : null;
      })();
  // Серии, которые ещё не вышли: вместо кадра у них заглушка «Скоро выйдет».
  // «Сегодня» берётся на каждый рендер — счёт на два десятка серий ничего не
  // стоит, а открытая через полночь страница не держит вчерашний день.
  const shownSeason = item?.seasons.find((entry) => entry.number === season) ?? null;
  const soonEpisodes = upcomingEpisodes(episodes ?? [], shownSeason?.airDate);
  const soonArt = soonEpisodes.size === 0
    ? null
    : soonImage({
        seasonPoster: shownSeason?.poster?.url,
        episodes: episodes ?? [],
        upcoming: soonEpisodes,
        backdrop: item?.backdrop?.url,
        poster: item?.poster?.url,
      });
  const searching = active.status === 'loading';
  // Какие ряды есть в панели выбора на шаге источников.
  const seasonRowShown = item !== null && item.kind === 'tv' && item.seasons.length > 0;
  const onlineRowsShown = activeKind === 'online'
    && (active.status === 'ready' || active.status === 'loading') && active.sources.length > 0;
  const trackerRowShown = activeKind === 'torrent' && active.status === 'ready' && trackers.length > 1;

  const stageSource = React.useMemo<StageSource | null>(
    () =>
      item === null
        ? null
        : {
            key: item.id,
            art: item.backdrop?.url ?? null,
            tiny: tinyImage(item.poster?.url ?? item.backdrop?.url ?? null),
            poster: item.poster?.url ?? null,
            wash: washImage(item.backdrop?.url, item.poster?.url),
          },
    [item],
  );

  /**
   * Список серий открывается на той, с которой продолжают, — ОДИН раз за
   * заход на шаг и только когда известно всё: прогресс, запомненный выбор и
   * серии именно того сезона. Раньше эффект срабатывал на что успело
   * прийти, и при другом порядке ответов оставался на вкладке сезона. Ручной
   * выбор сезона отменяет докрутку: зритель уже сам решил, куда смотреть.
   */
  useEffect(() => {
    if (step !== 'episodes') {
      episodesRevealedRef.current = false;
      return;
    }
    if (episodesRevealedRef.current || episodes === null || !progressReady || !choiceReady) return;
    if (lastEpisode === null) {
      episodesRevealedRef.current = true;
      return;
    }
    // Нужный сезон ещё не открыт (или его серии ещё едут) — ждём их.
    if (season !== lastEpisode.season) return;
    episodesRevealedRef.current = true;
    const target = resumeEpisodeRef.current;
    if (target === null) return;
    target.scrollIntoView?.({ block: 'nearest', inline: 'center' });
    // Фокус — тем, кто пришёл с клавиатуры или пульта: следующий Enter должен
    // включать именно эту серию. Мыши и пальцу рамка на карточке не нужна.
    if (lastInputWasKeyboard()) target.focus({ preventScroll: true });
  }, [step, episodes, season, progressReady, choiceReady, lastEpisode?.season, lastEpisode?.episode]);

  // Описание — столько строк, сколько помещается рядом с постером: колонка
  // текста не выше постера, и ряд кнопок стоит низом по его низу. До отрисовки,
  // чтобы лишние строки не мелькнули.
  const overviewRef = useRef<HTMLParagraphElement | null>(null);
  const overviewText = step === 'overview' ? (item?.overview ?? '') : '';
  useLayoutEffect(() => {
    const overview = overviewRef.current;
    return overview === null ? undefined : installOverviewFit(overview);
  }, [overviewText, item?.id]);

  return (
    <main className="app title-page">
      {/* Фон — размытый кадр тайтла (маленькая копия backdrop, нет его —
          постера), приглушённый к низу и к левому краю, с отсветом цвета
          постера: кадр угадывается, но не спорит с текстом. Сам постер —
          слева, рамкой, как карточка, и только на обзоре. */}
      {item && <TitleStage source={stageSource} className="title-stage" soft />}

      {/* Шапка. На обзоре — капсула «‹ Назад» над постером. На шагах выбора —
          одна строка, как в плеере: круглая «‹» и название тайтла, больше
          ничего (решение владельца): высота экрана нужна сериям и раздачам. */}
      <header className={step === 'overview' ? 'header title-header' : 'header title-header is-step'}>
        {step === 'overview' ? (
          <button type="button" className="back" onClick={back}>
            <BackIcon className="action-icon" />
            Назад
          </button>
        ) : (
          <button type="button" className="back is-round" onClick={back} aria-label="Назад" title="Назад">
            <BackIcon className="action-icon" />
          </button>
        )}
        {step === 'overview' && (
          <span className="brand">
            <LumosMark className="brand-mark" />
            Lumos
          </span>
        )}
        {step !== 'overview' && item && <h1 className="title-name" title={item.title}>{item.title}</h1>}
        {step !== 'overview' && !item && !error && <span className="skeleton-block title-skeleton-name" />}
      </header>

      {error && (
        <div className="state error state-retry">
          <span>{error}</span>
          <button type="button" className="secondary-action" onClick={() => setDetailsAttempt((value) => value + 1)}>
            Повторить
          </button>
        </div>
      )}
      {/* Заглушка той же формы, что и карточка: постер, название, строка,
          описание, кнопки. Приехавшая карточка встаёт на её место, ничего не
          сдвигая. На шагах выбора название стоит в шапке, а под ним — ряды
          выбора: заглушка повторяет их. */}
      {!item && !error && step === 'overview' && (
        <div className="title-layout title-skeleton-layout" aria-busy="true" aria-label="Загрузка…">
          <div className="title-aside">
            <span className="skeleton-block title-poster" />
          </div>
          <div className="title-body is-overview title-skeleton">
            <span className="skeleton-block title-skeleton-name" />
            <span className="skeleton-block title-skeleton-meta" />
            <span className="skeleton-block title-skeleton-line" />
            <span className="skeleton-block title-skeleton-line" />
            <span className="skeleton-block title-skeleton-line is-short" />
            <span className="title-skeleton-actions">
              <span className="skeleton-block title-skeleton-button" />
              <span className="skeleton-block title-skeleton-button" />
            </span>
          </div>
        </div>
      )}
      {!item && !error && step !== 'overview' && (
        <div className="title-layout is-step" aria-busy="true" aria-label="Загрузка…">
          <Loader variant="area" label="Загрузка…" />
        </div>
      )}

      {item && (
        <article className="title">
          <div className={step === 'overview' ? 'title-layout' : 'title-layout is-step'}>
            {/* Постер — слева, рамкой, как карточка в ряду; нет картинки —
                та же подложка с первой буквой, что у карточки. Только на
                обзоре: на шагах выбора источника и серии ширина нужна
                спискам (владелец). */}
            {/* Под постером — оригинальное название, если оно отличается от
                нашего (правка владельца 03.10.2026): тихо, до двух строк, по
                ширине постера. Нет оригинала — под постером ничего. Жанры
                вернулись в правую колонку, сразу под название. */}
            {step === 'overview' && (
              <div className="title-aside">
              <div className="title-poster">
                {item.poster?.url ? (
                  <img
                    src={item.poster.url}
                    srcSet={posterSrcSet(item.poster.url)}
                    sizes="(max-width: 768px) 132px, (max-width: 1280px) 340px, 30vw"
                    alt=""
                    decoding="async"
                  />
                ) : (
                  <span className="poster-initial" aria-hidden="true">{item.title.slice(0, 1)}</span>
                )}
              </div>
              {item.originalTitle && item.originalTitle !== item.title && (
                <p className="title-original" title={item.originalTitle}>{item.originalTitle}</p>
              )}
              </div>
            )}
            <div className={step === 'overview' ? (showProgress ? 'title-body is-overview has-progress' : 'title-body is-overview') : 'title-body'}>
              {/* Название, строка «год · вид · длительность · оценка» и жанры —
                  одним блоком (правка владельца 05.10.2026): строка с годом
                  стоит СРАЗУ под названием, жанры — после неё. На телефоне
                  этот блок встаёт справа от постера.

                  Название — ТЕКСТОМ, обычным шрифтом и в обычном регистре, а не
                  фирменным логотипом (владелец): на странице фильма название
                  читают, а не узнают по знаку, и логотипы TMDB слишком разные —
                  от узкой строки 15 : 1 до плашки без прозрачного фона. Логотип
                  остаётся на главной и в плеере.

                  На шагах выбора название уезжает в шапку, к «‹»: там разговор
                  уже о том, ЧЕМ смотреть, и строке с годом места нет. */}
              {step === 'overview' && (
                <div className="title-head">
                  <h1 className="title-name" data-length={titleLength(item.title)} title={item.title}>
                    {item.title}
                  </h1>
                  <div className="meta">
                    {item.releaseYear !== null && <span>{item.releaseYear}</span>}
                    {/* Страны производства — после года, перед «Сериал/Фильм»
                        (правка владельца 05.10.2026). В строке не больше трёх:
                        дальше она превращается в перечень. */}
                    {countriesOf(item).length > 0 && (
                      <span className="meta-countries" title={countriesOf(item).join(', ')}>
                        {countriesOf(item).slice(0, 3).join(', ')}
                      </span>
                    )}
                    <span>{KIND_LABEL[item.kind]}</span>
                    {item.runtimeMinutes && <span>{formatRuntime(item.runtimeMinutes)}</span>}
                    {item.ratings.average != null && (
                      <span className="rating">★ {item.ratings.average.toFixed(1)}</span>
                    )}
                  </div>
                  {item.genres.length > 0 && (
                    <div className="chip-row title-genres">
                      {item.genres.map((g) => (
                        <span className="chip" key={g.id}>
                          {g.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Описание и кнопки — только пока выбор ещё не сделан. Как только
                  зритель нажал «Смотреть онлайн» или «Торренты», разговор идёт
                  уже о том, ЧЕМ смотреть: описание, которое он только что
                  прочитал, отнимает у списка полэкрана. */}
              {step === 'overview' && (
                <>
              {item.tagline && <p className="tagline">{item.tagline}</p>}
              {/* Описание — не длиннее отведённого числа строк (их больше на
                  высоком экране); целиком — во всплывающей подсказке. */}
              {item.overview !== '' && <p ref={overviewRef} className="overview" title={item.overview}>{item.overview}</p>}

              {/* Кнопки — отдельной зоной у нижнего края постера, а не вплотную
                  к описанию (владелец: «всё в куче»). Колонка растягивается до
                  высоты постера, и лишнее место встаёт МЕЖДУ описанием и
                  кнопками; короткое описание или пустое — кнопки всё равно
                  внизу, длинное — отодвигает их, а не наезжает. */}
              <div className={showProgress ? 'title-cta has-progress' : 'title-cta'}>
              <div className="title-actions">
                {/* «Продолжить» — ДЕЙСТВИЕ, а не подпись.
                    Раньше здесь стояла строчка «Продолжить с 1:23» рядом с
                    описанием: она сообщала, что просмотр не закончен, но нажать
                    на неё было нельзя, и продолжать приходилось теми же тремя
                    шагами, что и смотреть с начала. Кнопка ведёт ровно туда, где
                    человек остановился: у сериала — к его серии. */}
                {/* Сколько уже посмотрено — полосой СТРОГО под кнопкой, по её
                    центру (владелец, 05.10.2026): кнопка и полоса — одна вещь,
                    поэтому стоят в общей обёртке, и ширина полосы идёт от
                    ширины кнопки — её прямой кромки, без скруглений. Тот же
                    факт, что полоса на карточке в «Продолжить просмотр».
                    У следующей серии позиции ещё нет — полосы тоже. */}
                {resume && (
                  <div className="title-resume">
                    <button
                      type="button"
                      className="primary-action"
                      onClick={() => void continueWatching()}
                      disabled={search.online.status === 'loading'}
                    >
                      {search.online.status === 'loading' ? 'Поиск…' : (
                        <>
                          <PlayIcon className="action-icon" />
                          <span className="action-label">Продолжить{resumeLabel}</span>
                        </>
                      )}
                    </button>
                    {showProgress && (
                      <div className="title-progress">
                        <span className="title-progress-track" aria-hidden="true">
                          <span className="title-progress-fill" style={{ transform: `scaleX(${watchedPercent / 100})` }} />
                        </span>
                        <span className="title-progress-note">
                          {resume.durationMs !== null && resume.durationMs > resume.positionMs
                            ? `Осталось ${formatRuntime(Math.max(1, Math.round((resume.durationMs - resume.positionMs) / 60_000)))}`
                            : `Просмотрено ${Math.round(watchedPercent)} %`}
                        </span>
                      </div>
                    )}
                  </div>
                )}
                {/* Кнопка про онлайн — только пока смотреть НЕ начинали.
                    Начали — есть «Продолжить», и второй кнопки не нужно: у
                    сериала она вела бы в тот же список, только не на ту серию,
                    а у фильма предлагала бы смотреть сначала — то, чего почти
                    никто не хочет, но что стояло первым под рукой. Пересмотреть
                    с начала по-прежнему можно: «Сбросить прогресс» в меню
                    карточки на постере. */}
                {!resume && (
                  <button
                    type="button"
                    className="primary-action"
                    onClick={() => void loadSources('online')}
                    disabled={search.online.status === 'loading'}
                  >
                    {search.online.status === 'loading' ? 'Поиск…' : (
                      <>
                        <PlayIcon className="action-icon" />
                        Смотреть онлайн
                      </>
                    )}
                  </button>
                )}
                <button
                  type="button"
                  className="secondary-action"
                  onClick={() => void loadSources('torrent')}
                  disabled={search.torrent.status === 'loading'}
                >
                  {search.torrent.status === 'loading' ? 'Поиск…' : 'Торренты'}
                </button>
                {/*
                  Закладка — знаком, а не словами. Рядом стоят кнопки действий
                  («Смотреть», «Торренты»), и третья такая же по виду читалась
                  как ещё один способ включить кино, а не как «отложить к себе».
                  Знак закладки говорит это сам; подпись остаётся во всплывающей
                  и для чтения с экрана.
                */}
                <button
                  type="button"
                  className={favorite ? 'icon-action active' : 'icon-action'}
                  onClick={() => void toggleFavorite()}
                  disabled={favoriteBusy}
                  aria-pressed={favorite}
                  aria-label={favorite ? 'Убрать из избранного' : 'В избранное'}
                  title={favorite ? 'Убрать из избранного' : 'В избранное'}
                >
                  {favorite ? (
                    <FavoritesIconFilled className="action-icon" />
                  ) : (
                    <FavoritesIcon className="action-icon" />
                  )}
                </button>
              </div>
              {/* Под кнопками — в строке под нижним краем постера, рядом с
                  оригинальным названием: сами кнопки стоят низом ровно по
                  низу постера, и эта строка их не сдвигает. */}
              <div className="title-cta-notes">
              {favoriteError && <p className="playback-note error">{favoriteError}</p>}
              </div>
              </div>
                </>
              )}

              {/* Выбор источника — ЗДЕСЬ, сразу под названием, а не полосой под
                  всей карточкой. Это продолжение того же разговора: нажал
                  «Смотреть онлайн» — рядом с кнопкой и появились витрины.
                  Внизу страницы список выглядел отдельным разделом, к которому
                  ещё надо прокрутиться. */}
          {step === 'sources' && (
              <div className="sources" id="sources">
                {/* Заголовка у шага нет ни у онлайна, ни у раздач (правка
                    владельца 03.10.2026): название тайтла стоит в шапке, а
                    «Торренты · сезон 1» повторяли то, что видно и так — сезон
                    отмечен капсулой ниже, а путь сюда зритель прошёл сам.

                    Всё, из чего выбирают, — одной панелью рядов с общей
                    колонкой подписей: сезон, источник и озвучка (онлайн) или
                    трекер (раздачи). Порядок рядов и сама логика выбора
                    прежние. */}
                {(seasonRowShown || onlineRowsShown || trackerRowShown) && (
                  <div className="chooser">
                    {seasonRowShown && (
                      <ChooserRow label="Сезон">
                        {item.seasons.map((entry) => (
                          <button
                            key={entry.number}
                            type="button"
                            className={entry.number === season ? 'capsule active' : 'capsule'}
                            onClick={() => chooseSeason(entry.number)}
                          >
                            {seasonLabel(entry.number)}
                          </button>
                        ))}
                      </ChooserRow>
                    )}
                    {onlineRowsShown && (
                      <>
                        <ChooserRow label="Источник">
                          {active.sources.map((s) => (
                            <button
                              key={s.sourceId}
                              type="button"
                              className={s.sourceId === selectedSource?.sourceId ? 'capsule active' : 'capsule'}
                              onClick={() => void selectSource(s)}
                            >
                              {s.providerLabel ?? s.title}
                            </button>
                          ))}
                          {/* Ждём и ПЕРВЫЙ ответ, а не только догрузку: до него
                              сервер ещё ничего не сказал про `pending`, и пустой
                              ряд без знака ожидания выглядел как «не грузится». */}
                          {(active.pending || active.status === 'loading') && (
                            <span className="capsule static chooser-loading">
                              <SunLoader className="sun-loader-inline" />
                              {active.sources.length === 0 ? 'Поиск…' : 'ещё…'}
                            </span>
                          )}
                        </ChooserRow>
                        {selectedSource !== null && (
                          // Карточка дорожки есть всегда, даже когда витрина отдаёт
                          // одну: именно нажатие по ней и начинает просмотр. Отдельная
                          // кнопка «Смотреть» после этого была бы вторым способом
                          // сделать то же самое.
                          //
                          // Но не РАНЬШЕ, чем витрина ответит. Пока список едет,
                          // карточка обещала бы выбор, которого ещё нет, а нажатие
                          // по ней означало бы «первая попавшаяся дорожка».
                          <ChooserRow label="Озвучка" body="plain">
                            {voicesPending ? (
                              <Loader variant="row" label={VOICES_PROGRESS} className="voices-loader" />
                            ) : (
                              <VoiceCards
                                voices={voices.length > 0 ? voices : [SINGLE_TRACK]}
                                selected={selectedVoice}
                                onSelect={(voice) => void playWithVoice(voice)}
                                image={item.backdrop?.url ?? item.poster?.url ?? null}
                                progressPercent={watchedPercent}
                              />
                            )}
                          </ChooserRow>
                        )}
                      </>
                    )}
                    {/*
                      Фильтр по трекеру. Раздачи с разных трекеров отличаются не
                      только качеством: где-то свои релиз-группы, где-то свои
                      озвучки, — и «покажи только отсюда» это первое, что хочется
                      сделать в списке из восьмидесяти строк. «Все» первым и по
                      умолчанию: сузить проще, чем догадаться, что список урезан.
                    */}
                    {trackerRowShown && (
                      <ChooserRow label="Трекер" body="strip">
                        <button
                          type="button"
                          className={tracker === null ? 'capsule active' : 'capsule'}
                          onClick={() => setTracker(null)}
                        >
                          Все <span className="capsule-count">{active.sources.length}</span>
                        </button>
                        {trackers.map(([name, count]) => (
                          <button
                            key={name}
                            type="button"
                            className={tracker === name ? 'capsule active' : 'capsule'}
                            onClick={() => setTracker(name)}
                          >
                            {name} <span className="capsule-count">{count}</span>
                          </button>
                        ))}
                      </ChooserRow>
                    )}
                  </div>
                )}

                {activeKind === 'online' ? (
                  // Онлайн-фильм: источник и озвучка — в панели выше, здесь
                  // только то, что бывает вместо неё.
                  <>
                    {active.status === 'error' && (
                      <SearchRetry
                        text="Не удалось загрузить источники."
                        error
                        onRetry={() => void loadSources(activeKind, undefined, { keepStep: true })}
                      />
                    )}
                    {active.status === 'empty' && (
                      <SearchRetry
                        text={active.notice ?? ONLINE_NOT_FOUND}
                        onRetry={() => void loadSources(activeKind, undefined, { keepStep: true })}
                      />
                    )}
                    {/* Пока не пришёл ни один источник — крупный знак по центру
                        пустого места; первый пришедший источник сменяет его
                        панелью, а догрузку показывает капсула «ещё…» в ряду. */}
                    <ChooserSearch
                      active={(active.status === 'loading' || active.pending) && active.sources.length === 0}
                      caption={SEARCH_PROGRESS[activeKind]}
                    />
                  </>
                ) : (
                  // Файлы-релизы: раздачи и Jellyfin с деталями (размер, сиды,
                  // кодек) — их удобнее видеть строками, а не в выпадашке.
                  <>
                    {active.status === 'idle' && <p className="state">{SEARCH_HINT[activeKind]}</p>}
                    <ChooserSearch active={active.status === 'loading'} caption={SEARCH_PROGRESS[activeKind]} />
                    {active.status === 'error' && (
                      <SearchRetry
                        text="Не удалось загрузить источники."
                        error
                        onRetry={() => void loadSources(activeKind, undefined, { keepStep: true })}
                      />
                    )}
                    {active.status === 'empty' && (
                      <SearchRetry
                        text={active.notice ?? 'Источники не найдены.'}
                        onRetry={() => void loadSources(activeKind, undefined, { keepStep: true })}
                      />
                    )}
                    {active.status === 'ready' && (
                      <ul className="source-list">
                        {visibleSources.map((s, i) => (
                          <SourceRow
                            source={s}
                            index={i}
                            key={s.sourceId}
                            onPlay={(source) => void chooseTorrent(source)}
                            busy={playbackBusy}
                            isSeries={item.kind === 'tv'}
                            selected={selectedSource?.sourceId === s.sourceId}
                            played={isPlayed(s)}
                            onToggleStar={toggleStar}
                          />
                        ))}
                      </ul>
                    )}
                  </>
                )}
                {playbackMessage && <p className="playback-note">{playbackMessage}</p>}
              </div>
              )}

          {step === 'episodes' && item.kind === 'tv' && item.seasons.length > 0 && (
                <div className="seasons" id="episodes">
                  {/*
                    Всё управление собрано компактной панелью селекторов (Сезон ·
                    Источник · Озвучка), а серии идут сразу под ней — как фильтр-
                    строка в Lampa, вместо разбросанных рядов капсул.
                  */}
                  <div className="chooser">
                    {item.seasons.length > 1 && (
                      <ChooserRow label="Сезон">
                        {item.seasons.map((entry) => (
                          <button
                            key={entry.number}
                            type="button"
                            className={entry.number === season ? 'capsule active' : 'capsule'}
                            onClick={() => chooseSeason(entry.number)}
                          >
                            {seasonLabel(entry.number)}
                          </button>
                        ))}
                      </ChooserRow>
                    )}
                    {/*
                      Выбор источника здесь — только для ОНЛАЙНА: там источник и
                      озвучка перебираются на лету, прямо у списка серий.
                      У торрентов раздача выбрана шагом раньше, и повторять её
                      выбор нечем: капсул было бы столько же, сколько раздач
                      (восемьдесят с лишним), и шаг «выбери серию» превращался бы во
                      второй «выбери раздачу». Показываем выбранное и путь назад.
                    */}
                    {activeKind === 'online' && (active.sources.length > 0 || searching || active.pending) && (
                      <ChooserRow label="Источник">
                        {active.sources.map((entry) => (
                          <button
                            key={entry.sourceId}
                            type="button"
                            className={entry.sourceId === selectedSource?.sourceId ? 'capsule active' : 'capsule'}
                            onClick={() => void selectSource(entry)}
                          >
                            {entry.providerLabel ?? entry.title}
                          </button>
                        ))}
                        {active.pending && (
                          <span className="capsule static chooser-loading">
                            <SunLoader className="sun-loader-inline" />
                            {active.sources.length === 0 ? 'Поиск…' : 'ещё…'}
                          </span>
                        )}
                      </ChooserRow>
                    )}
                    {activeKind === 'torrent' && selectedSource !== null && (
                      <ChooserRow label="Раздача">
                        <span className="capsule static chosen-source">
                          {selectedSource.providerLabel && (
                            <span className="chip chip-accent">{selectedSource.providerLabel}</span>
                          )}
                          <span className="chosen-source-title">
                            {selectedSource.releaseName ?? selectedSource.title}
                          </span>
                        </span>
                        <button type="button" className="capsule" onClick={() => setStep('sources')}>
                          Сменить
                        </button>
                      </ChooserRow>
                    )}
                    {(voicesPending || voices.length > 1) && (
                      <ChooserRow label="Озвучка" body="plain">
                        {voicesPending ? (
                          // Пустое место на этом ряду читалось бы как «выбора нет», и
                          // зритель нажимал бы серию, не дождавшись своей озвучки.
                          // Знак — посередине ряда, высота ряда — как у карточек.
                          <Loader variant="row" label={VOICES_PROGRESS} className="voices-loader" />
                        ) : (
                          <VoiceCards
                            voices={voices}
                            selected={selectedVoice}
                            onSelect={chooseVoice}
                            image={item.backdrop?.url ?? item.poster?.url ?? null}
                            progressPercent={watchedPercent}
                          />
                        )}
                      </ChooserRow>
                    )}
                  </div>

                  {active.status === 'empty' && (
                    <SearchRetry
                      text={active.notice ?? (activeKind === 'online' ? ONLINE_NOT_FOUND : 'Источники не найдены.')}
                      onRetry={() => void loadSources(activeKind, undefined, { keepStep: true })}
                    />
                  )}


                  {/* Серии едут — знак посередине их будущего ряда: высота ряда
                      карточек зарезервирована, пришедшие серии ничего не сдвинут. */}
                  {episodes === null && <Loader variant="row" label="Загрузка серий…" className="episodes-loader" />}
                  {episodes !== null && episodes.length === 0 && (
                    <p className="state">{episodesNotice ?? 'Серии не найдены.'}</p>
                  )}
                  {episodes !== null && episodes.length > 0 && (
                    <ul className="episode-grid">
                      {episodes.map((episode) => (
                        <li
                          key={`${episode.seasonNumber}-${episode.episodeNumber}`}
                          className={lastEpisode?.season === episode.seasonNumber
                            && lastEpisode.episode === episode.episodeNumber
                            ? 'episode-card current'
                            : 'episode-card'}
                          onContextMenu={(event) => {
                            event.preventDefault();
                            setEpisodeMenu({
                              season: episode.seasonNumber,
                              episode: episode.episodeNumber,
                            });
                          }}
                        >
                          <button
                            type="button"
                            className="episode-card-button"
                            disabled={playbackBusy}
                            onClick={() => void playEpisode(episode)}
                            // Экран открывается на той серии, с которой продолжают:
                            // список бывает в тридцать карточек, и искать в нём
                            // глазами, где остановился, — работа, которую страница
                            // должна делать сама.
                            ref={lastEpisode?.season === episode.seasonNumber
                              && lastEpisode.episode === episode.episodeNumber
                              ? resumeEpisodeRef
                              : undefined}
                          >
                            <span className="episode-still">
                              {/*
                                Серия ещё не вышла — заглушка вместо кадра, даже
                                если кадр прислали: до выхода это промо или
                                пустышка. Карточка при этом обычная — нажимается
                                и встаёт в фокус как любая другая.
                              */}
                              {soonEpisodes.has(episode.episodeNumber) ? (
                                <EpisodeSoon
                                  image={soonArt}
                                  date={soonEpisodes.get(episode.episodeNumber)?.date ?? null}
                                />
                              ) : episode.still?.url ? (
                                <img src={episode.still.url} alt="" loading="lazy" />
                              ) : (
                                <span className="episode-still-placeholder">{episode.episodeNumber}</span>
                              )}
                              <span className="episode-badge">{episodeNumberLabel(episode.episodeNumber)}</span>
                              {/*
                                Полоса просмотренного — та же, что на постере в
                                «Продолжить просмотр»: по ней сразу видно, какая
                                серия начата и насколько. Досмотренные помечаем
                                полной полосой, а не прячем: список серий — не
                                очередь задач, к серии возвращаются.
                              */}
                              {progressOf(episodeProgress, episode) !== null && (
                                <span className="card-progress" aria-hidden="true">
                                  <span
                                    className="card-progress-fill"
                                    style={{ width: `${progressOf(episodeProgress, episode)}%` }}
                                  />
                                </span>
                              )}
                            </span>
                            <span className="episode-card-body">
                              <span className="episode-name" title={episodeLabel(episode.episodeNumber, episode.name)}>
                                {episodeLabel(episode.episodeNumber, episode.name)}
                              </span>
                              <span className="episode-meta">
                                {episode.airDate ?? ''}
                                {episode.airDate && episode.runtimeMinutes ? ' · ' : ''}
                                {episode.runtimeMinutes ? `${episode.runtimeMinutes} мин` : ''}
                              </span>
                              {episode.overview && (
                                <span className="episode-overview">{episode.overview}</span>
                              )}
                            </span>
                          </button>
                          {/*
                            Действия над ОДНОЙ серией. Правым щелчком — как на
                            постере в «Продолжить»: там этот жест уже означает
                            «что сделать с этой карточкой», и заводить для серий
                            второй способ незачем.

                            Кнопка-многоточие рядом — для тех, кто правым щелчком
                            не пользуется: на самой карточке места нет, а
                            действие должно быть достижимо без догадок.
                          */}
                          <button
                            type="button"
                            className="episode-menu-toggle"
                            aria-label={`Действия: серия ${episode.episodeNumber}`}
                            onClick={() => setEpisodeMenu(
                              episodeMenu?.season === episode.seasonNumber
                                && episodeMenu.episode === episode.episodeNumber
                                ? null
                                : { season: episode.seasonNumber, episode: episode.episodeNumber },
                            )}
                          >
                            …
                          </button>
                          {episodeMenu?.season === episode.seasonNumber
                            && episodeMenu.episode === episode.episodeNumber && (
                            <div className="card-menu" role="menu" aria-label={`Действия: серия ${episode.episodeNumber}`}>
                              <button
                                type="button"
                                role="menuitem"
                                onClick={() => void applyEpisodeAction('watched', episode)}
                              >
                                Просмотрено
                              </button>
                              <button
                                type="button"
                                role="menuitem"
                                onClick={() => void applyEpisodeAction('reset', episode)}
                              >
                                Сбросить прогресс
                              </button>
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                  {playbackMessage && <p className="playback-note">{playbackMessage}</p>}
                </div>
              )}

            </div>
          </div>

          {/* Актёры — в самом низу карточки: состав смотрят после того, как
              решили, чем смотреть, а не вместо этого. */}
          {/* Ряд лиц под описанием: по актёрскому составу тайтл узнают
              не хуже, чем по названию, а на постере видно не всех. */}
          {step === 'overview' && item.cast.length > 0 && (
            <section className="cast">
              {/* «Съёмочная группа и актёры», а не «Актёры»: первыми в ряду
                  теперь идут те, кто фильм сделал, — режиссёр, оператор,
                  сценарист. Прежний заголовок называл бы их актёрами. */}
              <h3>Создатели и актёры</h3>
              <div className="cast-row">
                {item.cast.map((person) => (
                  // Ссылка, а не карточка: «а что он ещё играл» — первый вопрос
                  // после «а кто это». Ссылкой, а не кнопкой, потому что это
                  // переход по адресу: работают и «в новой вкладке», и «назад».
                  <a
                    className="cast-card"
                    key={person.id}
                    href={`#/person/${encodeURIComponent(person.id)}`}
                    title={`Фильмография: ${person.name}`}
                  >
                    <div className="cast-photo">
                      {person.photo ? (
                        <img src={person.photo.url} alt="" loading="lazy" />
                      ) : (
                        // Фотографии нет — инициал: пустая рамка в ряду читается
                        // как незагрузившаяся картинка, то есть как поломка.
                        <span aria-hidden="true">{person.name.slice(0, 1).toUpperCase()}</span>
                      )}
                    </div>
                    <figcaption>
                      <span className="cast-name">{person.name}</span>
                      {person.role !== '' && <span className="cast-role">{person.role}</span>}
                    </figcaption>
                  </a>
                ))}
              </div>
            </section>
          )}

          {/* «Похожее» — ПОСЛЕ актёров и только на описании: это ответ на
              вопрос «а что ещё посмотреть», который возникает, когда с этим
              тайтлом уже разобрались. Ряд грузится отдельным запросом и не
              задерживает открытие карточки; пустой — просто не показывается. */}
          {step === 'overview' && (
            <>
              {/* Маркер вместо запроса при открытии: пока до него не долистали,
                  похожее не стоит ни запроса, ни ожидания. */}
              {!similarWanted && <div ref={similarSentinelRef} className="scroll-sentinel" />}
              {/* Франшиза показывается только когда она есть: у большинства
                  фильмов её нет вовсе, и место под ряд, который тут же
                  исчезнет, — обещание, которое мы почти всегда нарушаем.
                  Пусто — и «Похожее» идёт сразу. */}
              {collection !== null && collection.items.length > 0 && (
                <section className="catalog-section">
                  {/* Название вселенной — подзаголовком: «Из этой киновселенной»
                      отвечает на вопрос «что это за ряд», а «Гарри Поттер» — на
                      вопрос «какой именно». */}
                  <h2>
                    Из этой киновселенной
                    {collection.name !== null && <span className="section-note">{collection.name}</span>}
                  </h2>
                  <div className="rail">
                    {collection.items.map((entry) => (
                      <Card key={entry.id} item={entry} />
                    ))}
                  </div>
                </section>
              )}
              {similarWanted && similar === null && (
                <section className="catalog-section">
                  <h2>Похожее</h2>
                  <div className="rail" aria-hidden="true">
                    {[0, 1, 2, 3, 4, 5].map((slot) => (
                      <span className="card-skeleton skeleton-block" key={slot} />
                    ))}
                  </div>
                </section>
              )}
              {similar !== null && similar.length > 0 && (
                <section className="catalog-section">
                  <h2>Похожее</h2>
                  <div className="rail">
                    {similar.map((entry) => (
                      <Card key={entry.id} item={entry} />
                    ))}
                  </div>
                </section>
              )}
            </>
          )}

        </article>
      )}
    </main>
  );
}
